import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getUserProgress } from '../../config/api';
import { FactProgress } from '../../types/progress';
import { TRACK_RANGES } from '../../types/constants';
import { KPracticeSettings } from './types';
import NumberNamingPractice from './NumberNaming/NumberNamingPractice';
import SubitizingPractice from './Subitizing/SubitizingPractice';
import ComparisonPractice from './Comparison/ComparisonPractice';
import PartWholePractice from './PartWhole/PartWholePractice';
import AdditionWithin5Practice from './AdditionWithin5/AdditionWithin5Practice';
import PracticeStartScreen from './PracticeStartScreen';
import { motion } from 'framer-motion';
import UserProfileDropdown from '../Dashboard/UserProfileDropdown';

const KPracticePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [factsToPractice, setFactsToPractice] = useState<string[]>([]);
  const [currentPracticeType, setCurrentPracticeType] = useState<'learning' | 'accuracy' | 'fluency15' | 'fluency' | null>(null);
  const [practiceStarted, setPracticeStarted] = useState(false);
  const [currentTrack, setCurrentTrack] = useState<'TRACK_K_NAMING' | 'TRACK_K_SUBITIZING' | 'TRACK_K_COMPARING' | 'TRACK_K_PARTWHOLE' | 'TRACK_K_ADDITION_WITHIN_5' | null>(null);
  const [settings, setSettings] = useState<KPracticeSettings>({
    trackId: 'TRACK_K_NAMING',
    speechEnabled: true
  });

  useEffect(() => {
    const fetchAndFilterFacts = async () => {
      if (!user?.userId) return;

      try {
        setIsLoading(true);

        // Determine which track to use based on URL or progress
        const urlParams = new URLSearchParams(window.location.search);
        const trackParam = urlParams.get('track');

        let targetTrack: 'TRACK_K_NAMING' | 'TRACK_K_SUBITIZING' | 'TRACK_K_COMPARING' | 'TRACK_K_PARTWHOLE' | 'TRACK_K_ADDITION_WITHIN_5';

        if (trackParam === 'naming') {
          targetTrack = 'TRACK_K_NAMING';
        } else if (trackParam === 'subitizing') {
          targetTrack = 'TRACK_K_SUBITIZING';
        } else if (trackParam === 'comparing') {
          targetTrack = 'TRACK_K_COMPARING';
        } else if (trackParam === 'partwhole') {
          targetTrack = 'TRACK_K_PARTWHOLE';
        } else if (trackParam === 'additionwithin5') {
          targetTrack = 'TRACK_K_ADDITION_WITHIN_5';
        } else {
          // Default behavior - check tracks and prioritize based on needs
          const progressData = await getUserProgress(user.userId, 'TRACK_K_NAMING');
          const namingTrack = progressData.tracks.find(track => track.trackId === 'TRACK_K_NAMING');

          // Check if naming track has facts that need practice
          let hasNamingPracticeNeeds = false;
          if (namingTrack) {
            const [startFactId, endFactId] = TRACK_RANGES['TRACK_K_NAMING'];
            for (let factNum = startFactId; factNum <= endFactId; factNum++) {
              const factId = `FACT${factNum}`;
              const factProgress = namingTrack.facts[factId];
              if (!factProgress || factProgress.status === 'learning' || factProgress.status === 'accuracyPractice' || factProgress.status === 'fluency15Practice' || factProgress.status === 'fluency6Practice') {
                hasNamingPracticeNeeds = true;
                break;
              }
            }
          }

          if (hasNamingPracticeNeeds) {
            targetTrack = 'TRACK_K_NAMING';
          } else {
            // Check other tracks in sequence
            targetTrack = 'TRACK_K_SUBITIZING'; // Default fallback
          }
        }

        // Fetch progress for the determined track
        const progressData = await getUserProgress(user.userId, targetTrack);
        const kTrack = progressData.tracks.find(track => track.trackId === targetTrack);

        if (!kTrack) {
          // No progress for this K track - redirect to K assessment
          navigate('/k-assessment');
          return;
        }

        setCurrentTrack(targetTrack);
        setSettings(prev => ({ ...prev, trackId: targetTrack }));
        sessionStorage.setItem('activeTrackId', targetTrack);

        // Helper function to categorize a fact based on its progress
        // skipUnattempted: if true, skip facts with no progress (used for Comparing track)
        const categorizeFact = (
          factId: string,
          factProgress: FactProgress | undefined,
          factsToLearn: string[],
          factsForAccuracyPractice: string[],
          factsForFluency15Practice: string[],
          factsForFluencyPractice: string[],
          skipUnattempted: boolean = false
        ) => {
          // For tracks like Comparing, skip unattempted facts (only practice attempted but incorrect)
          if (!factProgress) {
            if (!skipUnattempted) {
              factsToLearn.push(factId);
            }
            // If skipUnattempted is true, don't add unattempted facts to any list
            return;
          }

          if (factProgress.status === 'learning') {
            factsToLearn.push(factId);
          } else {
            // Check accuracy regardless of status
            const lowAccuracy = factProgress.accuracyRate < 0.8;
            const hasNoTodayStats = !factProgress.todayStats || Object.keys(factProgress.todayStats).length === 0;

            if (lowAccuracy) {
              if (hasNoTodayStats && factProgress.status === 'accuracyPractice') {
                factsToLearn.push(factId);
              } else {
                factsForAccuracyPractice.push(factId);
              }
            } else if (factProgress.status === 'accuracyPractice') {
              factsForAccuracyPractice.push(factId);
            } else if (factProgress.status === 'fluency15Practice') {
              factsForFluency15Practice.push(factId);
            } else if (factProgress.status === 'fluency6Practice') {
              factsForFluencyPractice.push(factId);
            }
            // Mastered facts with good accuracy (≥80%) don't need practice
          }
        };

        // Categorize facts based on status and progress
        const factsToLearn: string[] = [];
        const factsForAccuracyPractice: string[] = [];
        const factsForFluency15Practice: string[] = [];
        const factsForFluencyPractice: string[] = [];

        // Collect all track facts for passing to accuracy/fluency15 screens
        const allTrackFacts: string[] = [];

        if (targetTrack === 'TRACK_K_NAMING') {
          // Number naming facts: digits 0-20
          const [startFactId, endFactId] = TRACK_RANGES['TRACK_K_NAMING'];
          for (let factNum = startFactId; factNum <= endFactId; factNum++) {
            const factId = `FACT${factNum}`;
            allTrackFacts.push(factId);
            const factProgress: FactProgress | undefined = kTrack.facts[factId];
            categorizeFact(factId, factProgress, factsToLearn, factsForAccuracyPractice, factsForFluency15Practice, factsForFluencyPractice);
          }
        } else if (targetTrack === 'TRACK_K_SUBITIZING') {
          // Subitizing facts: quantities 1-6
          const [startFactId, endFactId] = TRACK_RANGES['TRACK_K_SUBITIZING'];
          for (let factNum = startFactId; factNum <= endFactId; factNum++) {
            const factId = `FACT${factNum}`;
            allTrackFacts.push(factId);
            const factProgress: FactProgress | undefined = kTrack.facts[factId];
            categorizeFact(factId, factProgress, factsToLearn, factsForAccuracyPractice, factsForFluency15Practice, factsForFluencyPractice);
          }
        } else if (targetTrack === 'TRACK_K_COMPARING') {
          // Comparing facts: 50 comparison pairs
          // Skip unattempted facts - only practice attempted but incorrect facts
          const [startFactId, endFactId] = TRACK_RANGES['TRACK_K_COMPARING'];
          for (let factNum = startFactId; factNum <= endFactId; factNum++) {
            const factId = `FACT${factNum}`;
            allTrackFacts.push(factId);
            const factProgress: FactProgress | undefined = kTrack.facts[factId];
            categorizeFact(factId, factProgress, factsToLearn, factsForAccuracyPractice, factsForFluency15Practice, factsForFluencyPractice, true);
          }
        } else if (targetTrack === 'TRACK_K_PARTWHOLE') {
          // Part Whole facts: 0-5
          const [startFactId, endFactId] = TRACK_RANGES['TRACK_K_PARTWHOLE'];
          for (let factNum = startFactId; factNum <= endFactId; factNum++) {
            const factId = `FACT${factNum}`;
            allTrackFacts.push(factId);
            const factProgress: FactProgress | undefined = kTrack.facts[factId];
            categorizeFact(factId, factProgress, factsToLearn, factsForAccuracyPractice, factsForFluency15Practice, factsForFluencyPractice);
          }
        } else if (targetTrack === 'TRACK_K_ADDITION_WITHIN_5') {
          // Addition Within 5 facts: 12 facts
          const [startFactId, endFactId] = TRACK_RANGES['TRACK_K_ADDITION_WITHIN_5'];
          for (let factNum = startFactId; factNum <= endFactId; factNum++) {
            const factId = `FACT${factNum}`;
            allTrackFacts.push(factId);
            const factProgress: FactProgress | undefined = kTrack.facts[factId];
            categorizeFact(factId, factProgress, factsToLearn, factsForAccuracyPractice, factsForFluency15Practice, factsForFluencyPractice);
          }
        }

        // Determine which practice type to use
        // Priority order: learning → accuracy → fluency15 → fluency6
        if (factsToLearn.length >= 1) {
          setFactsToPractice(factsToLearn);
          setCurrentPracticeType('learning');
        } else if (factsForAccuracyPractice.length >= 1) {
          // For accuracy practice, pass ALL track facts (not just those in accuracyPractice status)
          setFactsToPractice(allTrackFacts);
          setCurrentPracticeType('accuracy');
        } else if (factsForFluency15Practice.length >= 1) {
          // For fluency15 practice, pass ALL track facts
          setFactsToPractice(allTrackFacts);
          setCurrentPracticeType('fluency15');
        } else if (factsForFluencyPractice.length >= 1) {
          setFactsToPractice(factsForFluencyPractice);
          setCurrentPracticeType('fluency');
        } else {
          // No practice needed, redirect to assessment
          navigate('/k-assessment');
          return;
        }
      } catch (error: any) {
        console.error('Error fetching progress data:', error);
        // If no progress exists (404) or any data loading issue, redirect to assessment
        // so the student can start fresh rather than seeing a dead-end error screen
        if (error?.response?.status === 404 || error?.message?.includes('No progress')) {
          navigate('/k-assessment');
          return;
        }
        setError('Failed to load practice data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndFilterFacts();
  }, [user?.userId, navigate]);

  const handlePracticeComplete = () => {
    // Re-trigger fact evaluation to determine next steps
    window.location.reload();
  };

  const handleBack = () => {
    // Use full page reload to ensure fresh user data (including updated focusTrack)
    window.location.href = '/';
  };

  const handlePracticeStart = () => {
    setPracticeStarted(true);
  };

  const getPracticeTitle = (): string => {
    let trackName = 'Numbers';
    switch (currentTrack) {
      case 'TRACK_K_NAMING':
        trackName = 'Numbers';
        break;
      case 'TRACK_K_SUBITIZING':
        trackName = 'Dot Counting';
        break;
      case 'TRACK_K_COMPARING':
        trackName = 'Comparing Numbers';
        break;
      case 'TRACK_K_PARTWHOLE':
        trackName = 'Part Whole';
        break;
      case 'TRACK_K_ADDITION_WITHIN_5':
        trackName = 'Addition';
        break;
    }

    switch (currentPracticeType) {
      case 'learning':
        return `Let's Practice ${trackName}!`;
      case 'accuracy':
        return `Let's Practice ${trackName} Accuracy!`;
      case 'fluency15':
        return `Let's Practice ${trackName} Speed!`;
      case 'fluency':
        return `Let's Practice ${trackName} Fluency!`;
      default:
        return `Let's Practice ${trackName}!`;
    }
  };

  if (isLoading) {
    return (
      <div className="relative min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        {user?.role === 'admin' && (
          <div className="absolute top-4 right-4 z-50">
            <UserProfileDropdown />
          </div>
        )}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white p-8 rounded-2xl shadow-lg text-center"
        >
          <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading your practice session...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        {user?.role === 'admin' && (
          <div className="absolute top-4 right-4 z-50">
            <UserProfileDropdown />
          </div>
        )}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md"
        >
          <div className="text-6xl mb-4">📚</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Practice Session</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleBack}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Back to Dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  // Show start screen first, then practice screens
  if (!practiceStarted) {
    return (
      <div className="relative">
        {user?.role === 'admin' && (
          <div className="absolute top-4 right-4 z-50">
            <UserProfileDropdown />
          </div>
        )}
        <PracticeStartScreen
          title={getPracticeTitle()}
          onStart={handlePracticeStart}
          currentTrack={currentTrack}
        />
      </div>
    );
  }

  // Render appropriate practice component based on practice type and track
  if (currentTrack === 'TRACK_K_NAMING') {
    return (
      <div className="relative">
        {user?.role === 'admin' && (
          <div className="absolute top-4 right-4 z-50">
            <UserProfileDropdown />
          </div>
        )}
        <NumberNamingPractice
          settings={settings}
          factsToPractice={factsToPractice}
          onComplete={handlePracticeComplete}
          onBack={handleBack}
          initialPracticeType={currentPracticeType || 'learning'}
        />
      </div>
    );
  }

  if (currentTrack === 'TRACK_K_SUBITIZING') {
    return (
      <div className="relative">
        {user?.role === 'admin' && (
          <div className="absolute top-4 right-4 z-50">
            <UserProfileDropdown />
          </div>
        )}
        <SubitizingPractice
          settings={settings}
          factsToPractice={factsToPractice}
          onComplete={handlePracticeComplete}
          onBack={handleBack}
          initialPracticeType={currentPracticeType || 'learning'}
        />
      </div>
    );
  }

  if (currentTrack === 'TRACK_K_COMPARING') {
    return (
      <div className="relative">
        {user?.role === 'admin' && (
          <div className="absolute top-4 right-4 z-50">
            <UserProfileDropdown />
          </div>
        )}
        <ComparisonPractice
          settings={settings}
          factsToPractice={factsToPractice}
          onComplete={handlePracticeComplete}
          onBack={handleBack}
          initialPracticeType={currentPracticeType || 'learning'}
        />
      </div>
    );
  }

  if (currentTrack === 'TRACK_K_PARTWHOLE') {
    return (
      <div className="relative">
        {user?.role === 'admin' && (
          <div className="absolute top-4 right-4 z-50">
            <UserProfileDropdown />
          </div>
        )}
        <PartWholePractice
          settings={settings}
          factsToPractice={factsToPractice}
          onComplete={handlePracticeComplete}
          onBack={handleBack}
          initialPracticeType={currentPracticeType || 'learning'}
        />
      </div>
    );
  }

  if (currentTrack === 'TRACK_K_ADDITION_WITHIN_5') {
    return (
      <div className="relative">
        {user?.role === 'admin' && (
          <div className="absolute top-4 right-4 z-50">
            <UserProfileDropdown />
          </div>
        )}
        <AdditionWithin5Practice
          settings={settings}
          factsToPractice={factsToPractice}
          onComplete={handlePracticeComplete}
          onBack={handleBack}
          initialPracticeType={currentPracticeType || 'learning'}
        />
      </div>
    );
  }

  // For now, show a placeholder for other tracks
  return (
    <div className="relative min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
      {user?.role === 'admin' && (
        <div className="absolute top-4 right-4 z-50">
          <UserProfileDropdown />
        </div>
      )}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md"
      >
        <div className="text-6xl mb-4">🚧</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Practice Component Coming Soon</h2>
        <p className="text-gray-600 mb-6">
          Track: {currentTrack}<br />
          Practice Type: {currentPracticeType}<br />
          Facts to Practice: {factsToPractice.length}
        </p>
        <button
          onClick={handleBack}
          className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
        >
          Back to Dashboard
        </button>
      </motion.div>
    </div>
  );
};

export default KPracticePage;
